import * as Cmp from "@impero-com/common/utils/comparison";

import { UserTagCategoryOverview, UserTagOverview } from "api/generated/model";
import { TagAndCategoryList } from "components/tag/tagAndCategory";
import { Tag, TagCategory, TagsData } from "components/tag/types";
export function formatTagsAndCategoriesString(
    tagIds: string[],
    tagsData: TagsData
) {
    const tagsAndCategoriesNames = tagIds.map((tagId) => {
        const tagResource = tagsData.tags.find((res) => res.id === tagId);
        if (!tagResource) {
            throw new Error(`Tag resource with id ${tagId} not found`);
        }
        const tagName = tagResource.name;
        const categoryName = tagsData.tagCategories.find(
            (cat) => cat.id === tagResource.tagCategoryId
        )?.name;
        if (!categoryName) {
            throw new Error(`Category with id ${tagName} not found`);
        }
        return [categoryName, tagName];
    });
    return tagsAndCategoriesNames
        .flatMap(([categoryName, tagName]) => `{${categoryName} - ${tagName}}`)
        .join(", ");
}

export function tagsTSTFilterFn(tagIds: string[], filterValue: string[]) {
    if (!filterValue || filterValue.length === 0) {
        return true;
    }
    return tagIds.some((tagId) => filterValue.includes(tagId));
}

export function tagsTSTSortingFn(rowATags: string[], rowBTags: string[]) {
    const tagCountA = rowATags.length;
    const tagCountB = rowBTags.length;
    const byCount = tagCountA - tagCountB;
    if (byCount !== 0) {
        return byCount;
    }
    // Within the same tag count, break ties alphabetically (case-insensitive)
    return Cmp.undefinedLast(Cmp.string())(
        joinTagsForSort(rowATags),
        joinTagsForSort(rowBTags)
    );
}

function joinTagsForSort(tags: string[]): string | null {
    if (!tags.length) {
        return null;
    }
    return [...tags].sort(Cmp.string()).join(", ");
}

export function tagAndCategoryListDefaultTSTCell(
    tagIds: string[],
    tagsResource: Tag[],
    tagCategoriesResource: TagCategory[]
) {
    const tagIdsSet = new Set(tagIds);
    const tagCategoryIds = new Set();
    const tags = tagsResource.filter((t) => {
        if (tagIdsSet.has(t.id)) {
            tagCategoryIds.add(t.tagCategoryId);
            return true;
        }
        return false;
    });
    const tagCategories = tagCategoriesResource.filter((tc) =>
        tagCategoryIds.has(tc.id)
    );
    return <TagAndCategoryList tags={tags} tagCategories={tagCategories} />;
}

export function formatTagRecordAsString(
    tagRecord: Record<string, string[]>
): string {
    return Object.entries(tagRecord)
        .flatMap(([categoryName, tagNames]) =>
            tagNames.map((tagName) => `${categoryName} - ${tagName}`)
        )
        .join(", ");
}

export function extractTagsAndCategoriesFromIds(
    tagIds: string[],
    linkedTags: UserTagOverview[],
    linkedTagCategories: UserTagCategoryOverview[]
): { tags: Tag[]; tagCategories: TagCategory[] } {
    const tagIdSet = new Set(tagIds);
    const tagCategoryIds = new Set();

    const tags = linkedTags.filter((t) => {
        if (tagIdSet.has(t.id)) {
            tagCategoryIds.add(t.tagCategoryId);
            return true;
        }
        return false;
    });

    const tagCategories = linkedTagCategories
        .filter((tc) => tagCategoryIds.has(tc.id))
        .map((tc) => ({
            id: tc.id,
            name: tc.name,
        }));

    return { tags, tagCategories };
}

export function extractUniqueTagIds<T>(
    rows: T[],
    tagFieldAccessor: (row: T) => string[]
): string[] {
    const allTagIds = rows.flatMap(tagFieldAccessor);
    return Array.from(new Set(allTagIds));
}
